import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ZoomMeetingRequest {
  userId: number;
  topic: string;
  startTime?: string;
  duration?: number;
  agenda?: string;
  password?: string;
  attendees?: string[];
}

export interface ZoomMeetingResponse {
  id: number;
  host_id: string;
  topic: string;
  type: number;
  status: string;
  start_time: string;
  duration: number;
  timezone: string;
  agenda: string;
  created_at: string;
  start_url: string;
  join_url: string;
  password: string;
  h323_password: string;
  pstn_password: string;
  encrypted_password: string;
  settings: any;
}

export interface ZoomToken {
  id: number;
  user_id: number;
  company_id?: number;
  service: string;
  connected: boolean;
  scope: string;
  created_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class ZoomService {
  private baseUrl = environment.apiUrl;
  private zoomAuthSubject = new BehaviorSubject<boolean>(false);
  public zoomAuth$ = this.zoomAuthSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Get Zoom authentication URL
   */
  getAuthUrl(userId: number): Observable<{url: string}> {
    const params = new HttpParams().set('userId', userId.toString());
    return this.http.get<{url: string}>(`${this.baseUrl}/calendar/zoom-auth-url`, { params });
  }

  /**
   * Check if user has Zoom tokens
   */
  getZoomTokens(userId: number, companyId?: number): Observable<ZoomToken | null> {
    let params = new HttpParams().set('userId', userId.toString());
    if (companyId) {
      params = params.set('company_id', companyId.toString());
    }
    return this.http.get<ZoomToken | null>(`${this.baseUrl}/calendar/zoom-tokens`, { params });
  }

  /**
   * Create a Zoom meeting
   */
  createMeeting(meetingData: ZoomMeetingRequest): Observable<ZoomMeetingResponse> {
    return this.http.post<ZoomMeetingResponse>(`${this.baseUrl}/calendar/zoom-create-meeting`, meetingData);
  }

  /**
   * Update a Zoom meeting
   */
  updateMeeting(meetingId: string, meetingData: Partial<ZoomMeetingRequest>): Observable<any> {
    return this.http.put(`${this.baseUrl}/calendar/zoom-meeting/${meetingId}`, meetingData);
  }

  /**
   * Delete a Zoom meeting
   */
  deleteMeeting(meetingId: string, userId: number): Observable<any> {
    const params = new HttpParams().set('userId', userId.toString());
    return this.http.delete(`${this.baseUrl}/calendar/zoom-meeting/${meetingId}`, { params });
  }

  /**
   * Get meeting details
   */
  getMeeting(meetingId: string, userId: number): Observable<ZoomMeetingResponse> {
    const params = new HttpParams().set('userId', userId.toString());
    return this.http.get<ZoomMeetingResponse>(`${this.baseUrl}/calendar/zoom-meeting/${meetingId}`, { params });
  }

  /**
   * Remove Zoom authentication
   */
  removeZoomAuth(tokenId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/calendar/zoom-token/${tokenId}`);
  }

  /**
   * Disconnect Zoom for a user
   */
  disconnectZoom(userId: number): Observable<{ message: string; success: boolean }> {
    const params = new HttpParams().set('userId', userId.toString());
    return this.http.delete<{ message: string; success: boolean }>(
      `${this.baseUrl}/calendar/zoom-disconnect`,
      { params }
    );
  }

  /**
   * Check if user is authenticated with Zoom
   */
  async isAuthenticated(userId: number): Promise<boolean> {
    try {
      const token = await this.getZoomTokens(userId).toPromise();
      const isAuth = !!token?.connected;
      this.zoomAuthSubject.next(isAuth);
      return isAuth;
    } catch (error) {
      this.zoomAuthSubject.next(false);
      return false;
    }
  }

  /**
   * Update authentication status
   */
  updateAuthStatus(isAuthenticated: boolean): void {
    this.zoomAuthSubject.next(isAuthenticated);
  }

  /**
   * Redirect user to Zoom OAuth
   */
  async connectZoom(userId: number): Promise<void> {
    try {
      const response = await this.getAuthUrl(userId).toPromise();
      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Error getting Zoom auth URL:', error);
      throw error;
    }
  }

  /**
   * Generate meeting duration from start and end dates
   */
  calculateDuration(startDate: Date, endDate: Date): number {
    const diffMs = endDate.getTime() - startDate.getTime();
    return Math.ceil(diffMs / (1000 * 60)); // Convert to minutes
  }

  /**
   * Format meeting data for Zoom API
   */
  formatMeetingData(data: {
    title: string;
    startDate?: Date;
    endDate?: Date;
    notes?: string;
    attendees?: any[];
    userId: number;
    password?: string;
  }): ZoomMeetingRequest {
    const meetingData: ZoomMeetingRequest = {
      userId: data.userId,
      topic: data.title,
      agenda: data.notes,
      password: data.password,
    };

    if (data.startDate) {
      meetingData.startTime = data.startDate.toISOString();
      if (data.endDate) {
        meetingData.duration = this.calculateDuration(data.startDate, data.endDate);
      }
    }

    if (data.attendees && data.attendees.length > 0) {
      meetingData.attendees = data.attendees.map(attendee => attendee.email).filter(email => email);
    }

    return meetingData;
  }

  /**
   * Extract meeting ID from Zoom URL
   */
  extractMeetingIdFromUrl(url: string): string | null {
    const match = url.match(/\/j\/(\d+)/);
    return match ? match[1] : null;
  }

  /**
   * Generate a secure random password for meetings
   */
  generateMeetingPassword(length: number = 6): string {
    const chars = '0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Validate Zoom meeting URL format
   */
  isValidZoomUrl(url: string): boolean {
    const zoomUrlPattern = /^https:\/\/[a-zA-Z0-9-]+\.zoom\.us\/j\/\d+(\?pwd=.+)?$/;
    return zoomUrlPattern.test(url);
  }

  /**
   * Copy text to clipboard
   */
  copyToClipboard(text: string): Promise<boolean> {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(() => false);
  }

  /**
   * Format meeting info for display
   */
  formatMeetingInfo(meeting: ZoomMeetingResponse): {
    title: string;
    meetingId: string;
    joinUrl: string;
    password?: string;
    startTime?: string;
    duration?: number;
  } {
    return {
      title: meeting.topic,
      meetingId: meeting.id.toString(),
      joinUrl: meeting.join_url,
      password: meeting.password,
      startTime: meeting.start_time,
      duration: meeting.duration,
    };
  }

  /**
   * Get meeting join instructions (SMS-friendly format)
   */
  getMeetingInstructions(meeting: ZoomMeetingResponse): string {
    let instructions = `Join Zoom Meeting: ${meeting.join_url}`;
    instructions += ` Meeting ID: ${meeting.id}`;
    
    if (meeting.password) {
      instructions += ` Password: ${meeting.password}`;
    }
    
    return instructions;
  }
}